import _ from 'lodash'

import { dataTransformationService } from 'features/charts-v2/services/data-transformation-service'
import type { Chart } from 'features/charts-v2/types'
import type { MetricType } from 'features/charts-v2/types'
import type { TimeBucketOption } from 'features/charts-v2/types'
import { getMetricById } from 'features/charts-v2/types'
import { ChartTypeV2 } from 'features/charts-v2/types/chart-types'
import type { Breakdown } from 'features/charts-v2/types/filter-hash/v2'
import { VOLUME_LABEL_FOR } from 'features/charts-v2/utils/metric-format-utils'
import { isRidgelineMetric } from 'features/charts-v2/utils/ridgeline-bins'
import { timestampUtils } from 'features/charts-v2/utils/timestamp-utils'

/**
 * One breakdown column: `id` drives special-casing (e.g. the
 * sentiment-composition branch), `label` is the column header. Custom
 * breakdowns have no canonical id/label, so callers resolve both — standard
 * breakdowns via `getBreakdownById`, custom breakdowns from their `alias` —
 * rather than passing bare ids this module can't label. Use
 * `resolveExportBreakdownColumns` to build this from a `ChartPanel`.
 */
export interface ChartExportColumn {
  id: string
  label: string
  /** The breakdown the column came from. Only the Sankey export reads it: a
   *  grouped breakdown is two flow columns, which `id`/`label` cannot say. */
  breakdown?: Breakdown
}

export interface ChartExportTable {
  headers: string[]
  rows: (string | number)[][]
}

// Sentiment value ordering must mirror the render path's
// transformForSentimentStacking: values[0]=positive, values[1]=neutral,
// values[2]=negative. Export rows are emitted Negative → Neutral → Positive
// to match the Charts-v1 CSV.
const SENTIMENT_ROWS: ReadonlyArray<{ label: string; valueIndex: number }> = [
  { label: 'Negative', valueIndex: 2 },
  { label: 'Neutral', valueIndex: 1 },
  { label: 'Positive', valueIndex: 0 },
]

/**
 * Builds the sentiment-composition (stacking) chart's export table, matching
 * the Charts-v1 export: columns Theme, Sentiment, Volume, % Volume, with
 * three rows per theme (Negative, Neutral, Positive). Themes are ordered by
 * total volume descending. % Volume is recomputed locally as the
 * within-theme share, rendered to two decimal places.
 */
const buildSentimentExportTable = (chart: Chart): ChartExportTable => {
  const themeRows = chart.data
    .map((item) => {
      const themeName =
        item.category[0] !== null && item.category[0] !== undefined
          ? String(item.category[0])
          : 'N/A'
      const counts = SENTIMENT_ROWS.map(
        ({ valueIndex }) => item.values[valueIndex] ?? 0,
      )
      const themeTotal = counts.reduce((sum, count) => sum + count, 0)
      return { themeName, counts, themeTotal }
    })
    .sort((a, b) => b.themeTotal - a.themeTotal)

  const rows = themeRows.flatMap(({ themeName, counts, themeTotal }) =>
    SENTIMENT_ROWS.map(({ label }, rowIndex) => {
      const volume = counts[rowIndex]!
      const percent = themeTotal === 0 ? 0 : (volume / themeTotal) * 100
      return [themeName, label, volume, `${percent.toFixed(2)}%`]
    }),
  )

  return {
    headers: ['Theme', 'Sentiment', 'Volume', '% Volume'],
    rows,
  }
}

/**
 * Builds the ridgeline (distribution) export table: one row per breakdown
 * value and score bucket with the bucket's count and its share of the row.
 * The generic path would label the per-bucket count with the metric's name.
 */
const buildRidgelineExportTable = (
  chart: Chart,
  metric: 'sentiment' | 'rating',
  selectedBreakdowns: ChartExportColumn[],
): ChartExportTable => {
  const rows = dataTransformationService
    .transformForRidgeline(chart, metric, 'DESC')
    .flatMap((ridge) =>
      ridge.bins.map((bin) => [
        ...selectedBreakdowns.map(() => ridge.category ?? 'N/A'),
        bin.label,
        bin.count,
        `${(bin.pct * 100).toFixed(2)}%`,
      ]),
    )

  return {
    headers: [
      ...selectedBreakdowns.map((b) => b.label),
      metric === 'sentiment' ? 'Sentiment bucket' : 'Rating',
      VOLUME_LABEL_FOR[metric] ?? 'Count',
      '% of row',
    ],
    rows,
  }
}

/**
 * Builds the Sankey export table: one row per drawn ribbon, naming its two ends
 * as "<Dimension>: <value>" with the flow's size and average score. Built from
 * the same graph the chart draws, so an "Other" end is exported as drawn.
 */
const buildSankeyExportTable = (
  chart: Chart,
  selectedBreakdowns: ChartExportColumn[],
): ChartExportTable => {
  const graph = dataTransformationService.transformForSankey(
    chart,
    selectedBreakdowns.map((column) => column.breakdown ?? null),
  )
  const endName = (nodeId: string): string => {
    const node = graph.nodes.find((candidate) => candidate.id === nodeId)!
    const label = selectedBreakdowns[node.breakdownIndex]?.label ?? 'Breakdown'
    return `${node.level === 'group' ? `${label} group` : label}: ${node.label}`
  }
  return {
    headers: ['source', 'target', 'count', 'avg_score'],
    rows: graph.links.map((link) => [
      endName(link.source),
      endName(link.target),
      link.count,
      link.avg === null ? '' : Number(link.avg.toFixed(4)),
    ]),
  }
}

/**
 * Builds the export table for a Charts-2 chart — the row/header shape shared
 * by the CSV export (`exportChartAsCSV`) and the dashboards-2-1 PPTX
 * appendix. Returns `null` when there's nothing to export.
 *
 * @param chart - Chart data from the API
 * @param selectedMetrics - Array of selected metrics
 * @param selectedBreakdowns - Breakdown columns in backend category order
 *   (standard AND custom). Each carries the id used for special-casing and
 *   the header label. Build with `resolveExportBreakdownColumns`.
 * @param timeBucket - Active time bucket; when set, the backend appends the
 *   time-bucket value as the last category dimension, so a Time column is
 *   emitted after the breakdown columns.
 * @param chartType - Active chart type; the ridgeline's rows are
 *   `[breakdown, bin]` cells and the Sankey's are paths through its columns,
 *   so each needs its own table shape.
 */
export const buildChartExportTable = (params: {
  chart: Chart | null
  selectedMetrics: MetricType[]
  selectedBreakdowns: ChartExportColumn[]
  timeBucket: TimeBucketOption | null
  chartType?: ChartTypeV2
}): ChartExportTable | null => {
  const { chart, selectedMetrics, selectedBreakdowns, timeBucket, chartType } =
    params
  if (!chart || !chart.data || chart.data.length === 0) {
    return null
  }

  const ridgelineMetric = selectedMetrics[0]
  if (
    chartType === ChartTypeV2.RIDGELINE &&
    isRidgelineMetric(ridgelineMetric)
  ) {
    return buildRidgelineExportTable(chart, ridgelineMetric, selectedBreakdowns)
  }

  if (chartType === ChartTypeV2.SANKEY) {
    const sankeyTable = buildSankeyExportTable(chart, selectedBreakdowns)
    return sankeyTable.rows.length > 0 ? sankeyTable : null
  }

  // Sentiment-composition charts arrive in a "sentiment stacking" shape: a
  // single-domain category (theme) with one count per sentiment in the
  // values array. Detect it with the same predicate the render path uses,
  // and require a sentiment breakdown to be present so non-sentiment
  // multi-value charts fall through to the generic path below.
  const isSentimentComposition =
    selectedBreakdowns.some((b) => b.id === 'sentiment') &&
    dataTransformationService.isSentimentStackingData(chart)

  if (isSentimentComposition) {
    return buildSentimentExportTable(chart)
  }

  const rows = chart.data.map((item) => {
    const row: (string | number)[] = []

    // Breakdown columns (categories). The caller passes every breakdown in
    // backend category order — standard AND custom — so index alignment with
    // item.category holds and custom breakdowns get their own column rather
    // than shifting the standard columns onto the wrong values.
    selectedBreakdowns.forEach((_, index) => {
      const categoryValue = item.category[index]
      row.push(
        categoryValue !== null && categoryValue !== undefined
          ? String(categoryValue)
          : 'N/A',
      )
    })

    // Time column when a time bucket is active. The backend appends the time
    // bucket as the LAST category dimension (after every breakdown, standard
    // or custom), so derive the index from the emitted category length
    // rather than selectedBreakdowns.length.
    if (timeBucket) {
      const timeValue = item.category[item.category.length - 1]
      row.push(timestampUtils.formatTimestampForBucket(timeValue, timeBucket))
    }

    // Metric columns (values).
    selectedMetrics.forEach((_, index) => {
      const value = item.values[index]
      row.push(value !== null && value !== undefined ? value : 0)
    })

    return row
  })

  if (rows.length === 0) {
    return null
  }

  const headers = [
    ...selectedBreakdowns.map((b) => b.label),
    ...(timeBucket ? ['Time'] : []),
    ...selectedMetrics.map(
      // Prefer the canonical metric label; fall back to startCase for
      // unknown ids.
      (metric) => getMetricById(metric)?.label ?? _.startCase(metric),
    ),
  ]

  return { headers, rows }
}
